import { POD_EXPANSION } from './generated-pod-expansion.js';

// Canonical manufacturing intent. IDs were checked against Printful's live catalog.
// Print areas and positions are deliberately NOT guessed when authentication fails.
export const POD_REVISION = 1;
const tee = { productId: 71, productName: 'Unisex Staple T-Shirt | Bella + Canvas 3001', size: 'M', technique: 'DTG', productType: 'tee' };
export const POD_PRODUCTS = Object.freeze([
  { ...tee, slug: 'scorpion-chest-tee', variantId: 4012, color: 'White', placement: 'front', view: 'front', layout: 'left_chest', file: 'scorpion-main.png', mascot: 'scorpion', realPhoto: '/products/scorpion-chest-real.webp' },
  { ...tee, slug: 'scorpion-back-tee', variantId: 4012, color: 'White', placement: 'back', view: 'back', layout: 'back_large', file: 'scorpion-main.png', mascot: 'scorpion' },
  { ...tee, slug: 'jellyfish-hem-tee', variantId: 4012, color: 'White', placement: 'front', view: 'front', layout: 'lower_right', file: 'jellyfish.png', mascot: 'jellyfish' },
  { ...tee, slug: 'snake-chest-tee', variantId: 4017, color: 'Black', placement: 'front', view: 'front', layout: 'left_chest', file: 'snake.png', mascot: 'snake' },
  { ...tee, slug: 'snake-back-tee', variantId: 4017, color: 'Black', placement: 'back', view: 'back', layout: 'back_large', file: 'snake.png', mascot: 'snake' },
  { slug: 'puffer-mug', productId: 19, productName: 'White Glossy Mug', variantId: 1320, color: 'White', size: '11 oz', technique: 'SUBLIMATION', productType: 'mug', placement: 'default', view: 'print-surface', layout: 'center', file: 'puffer.png', mascot: 'puffer', realPhoto: '/products/puffer-mug-real.webp' },
  { slug: 'snake-tumbler', productId: 585, productName: 'Stainless Steel Tumbler', variantId: 15005, color: 'White', size: '20 oz', technique: 'UV', productType: 'tumbler', placement: 'default', view: 'print-surface', layout: 'center', file: 'snake.png', mascot: 'snake' },
].map(Object.freeze));
export const ALL_POD_PRODUCTS = Object.freeze([...POD_PRODUCTS, ...POD_EXPANSION.map(Object.freeze)]);
export const podIntentFor = slug => ALL_POD_PRODUCTS.find(p => p.slug === slug) || null;
const normal = value => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

export function strictVariantFor(catalog, cfg) {
  const product = catalog.product;
  if (Number(product?.id) !== cfg.productId || product.is_discontinued) throw new Error(`${cfg.slug}: unavailable product ${cfg.productId}`);
  if (product.title !== cfg.productName) throw new Error(`${cfg.slug}: product identity changed`);
  if (!product.techniques?.some(t => t.key === cfg.technique)) throw new Error(`${cfg.slug}: technique ${cfg.technique} unavailable`);
  const variant = catalog.variants?.find(v => Number(v.id) === cfg.variantId);
  if (!variant || Number(variant.product_id) !== cfg.productId || normal(variant.color) !== normal(cfg.color) || normal(variant.size) !== normal(cfg.size)) throw new Error(`${cfg.slug}: exact variant/color/size mismatch`);
  if (variant.in_stock === false) throw new Error(`${cfg.slug}: exact variant is out of stock; substitution prohibited`);
  if (!product.files?.some(f => f.type === cfg.placement)) throw new Error(`${cfg.slug}: unsupported placement ${cfg.placement}`);
  return variant;
}

export function assertPosition(position) {
  if (!position) throw new Error('Print position has not been verified');
  for (const key of ['area_width', 'area_height', 'width', 'height', 'left', 'top']) {
    if (!Number.isFinite(position[key]) || position[key] < 0) throw new Error(`Invalid position.${key}`);
  }
  const p = position;
  if (!p.width || !p.height || !p.area_width || !p.area_height || p.left + p.width > p.area_width || p.top + p.height > p.area_height) throw new Error('Design exceeds verified print area');
  return position;
}

// Preserve the pre-existing manufacturing layout intent, not the pseudo-photo
// compositing coordinates. Use the real file aspect ratio (never a guessed ratio).
export function positionFor(layout, area, dimensions) {
  const aw = Number(area?.width), ah = Number(area?.height);
  const ratio = Number(dimensions?.width) / Number(dimensions?.height);
  if (!(aw > 0 && ah > 0 && ratio > 0 && Number.isFinite(ratio))) throw new Error('Verified print area and print-file dimensions are required');
  let width, height, left, top;
  if (layout === 'left_chest') {
    width = Math.round(aw * .30); height = Math.round(width / ratio);
    left = Math.round(aw * .57); top = Math.round(ah * .14);
  } else if (layout === 'back_large') {
    width = Math.round(aw * .76); height = Math.round(width / ratio);
    if (height > ah * .58) { height = Math.round(ah * .58); width = Math.round(height * ratio); }
    left = Math.round((aw - width) / 2); top = Math.round(ah * .17);
  } else if (layout === 'lower_right') {
    width = Math.round(aw * .28); height = Math.round(width / ratio);
    left = Math.round(aw * .64); top = Math.round(ah * .64);
  } else if (layout === 'center') {
    width = Math.round(aw * .42); height = Math.round(width / ratio);
    if (height > ah * .58) { height = Math.round(ah * .58); width = Math.round(height * ratio); }
    left = Math.round((aw - width) / 2); top = Math.round((ah - height) / 2);
  } else throw new Error(`Unknown canonical layout: ${layout}`);
  return assertPosition({ area_width: aw, area_height: ah, width, height, left, top });
}

export function assertResolvedSpec(spec) {
  const cfg = podIntentFor(spec?.slug);
  if (!cfg || spec.revision !== POD_REVISION || spec.status !== 'verified') throw new Error('Manufacturing specification is not verified');
  for (const key of ['productId', 'variantId', 'color', 'size', 'technique', 'placement', 'view', 'layout', 'file']) {
    if (spec[key] !== cfg[key]) throw new Error(`Stale manufacturing specification: ${key}`);
  }
  if (!spec.printFile?.sha256 || !spec.printFile.url?.startsWith('https://') || !spec.printArea?.printfile_id) throw new Error('Missing verified print file/area');
  assertPosition(spec.position);
  if (JSON.stringify(spec.position) !== JSON.stringify(positionFor(spec.layout, spec.printArea, spec.printFile))) throw new Error('Position differs from canonical manufacturing intent');
  return spec;
}

export function mockupRequestFor(spec) {
  assertResolvedSpec(spec);
  return { variant_ids: [spec.variantId], format: 'jpg', width: 1600,
    files: [{ placement: spec.placement, image_url: spec.printFile.url, position: { ...spec.position } }] };
}

// Payload construction only: NEVER creates, confirms, or charges an order.
// The fulfillment service must recheck the print-file hash and current stock.
export function orderItemFor(spec, quantity = 1) {
  assertResolvedSpec(spec);
  if (!spec.orderReady || spec.mockup?.source !== 'printful' || spec.mockup?.view !== spec.view) throw new Error('Official matching mockup is not ready; ordering is blocked');
  if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100) throw new Error('Invalid quantity');
  return { variant_id: spec.variantId, quantity,
    files: [{ type: spec.placement, url: spec.printFile.url, position: { ...spec.position, limit_to_print_area: true } }] };
}

export function selectMockup(mockups, spec) {
  const candidates = [];
  for (const m of mockups || []) {
    if (m.placement !== spec.placement || !m.variant_ids?.some(id => Number(id) === spec.variantId)) continue;
    const wrong = spec.view === 'back' ? /\bfront\b/i : spec.view === 'front' ? /\bback\b/i : /$a/;
    if (m.mockup_url && !wrong.test(m.display_name || '')) candidates.push({ url: m.mockup_url, title: m.display_name || spec.placement, score: 30, placement: m.placement });
    for (const extra of m.extra || []) {
      const title = [extra.title, extra.option, extra.option_group].filter(Boolean).join(' ');
      if (!extra.url || wrong.test(title)) continue;
      const matchingView = spec.view === 'print-surface' ? /front|center|centre|angled|side|right/i.test(title) : new RegExp(`\\b${spec.view}\\b`, 'i').test(title);
      // Unlabelled lifestyle extras cannot prove which side is visible.
      if (!matchingView) continue;
      const score = 35 + (/flat|product|basic|on.?hanger/i.test(title) ? 20 : 0) - (/model|lifestyle|couple/i.test(title) ? 30 : 0);
      candidates.push({ url: extra.url, title, score, placement: m.placement });
    }
  }
  candidates.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));
  if (!candidates.length) throw new Error(`${spec.slug}: no matching ${spec.view} mockup for variant ${spec.variantId}`);
  return { ...candidates[0], view: spec.view, variantId: spec.variantId };
}
